/**
 * Auth V3 — headless self-repair of a missing offline device session.
 *
 * The stored session record ({@link loadDeviceSessionDetailed}) is the
 * non-secret half of a register's paired identity; the signing keypair in
 * IndexedDB is the other. Legacy logins, logout wipes, and definitive backend
 * rejections can leave a register keypair-intact but sessionless, and nothing
 * outside the login screen could recreate the record — permanently failing
 * offline-payment eligibility with `device-session-missing`.
 *
 * This service re-mints the record mid-session over the public,
 * signature-based refresh endpoints (the same flow the login screen's
 * `useDevicePairing.tryRefresh` uses) whenever the register is online,
 * authenticated as its business-device Firebase user, and the record is
 * genuinely absent. It deliberately skips `acceptDeviceToken`: Firebase is
 * already signed in as that exact UID, so the minted custom token is unused
 * and auth listeners never churn mid-sale.
 */

import { AuthV3ErrorClassification } from '@am3nd-org/pointcraft-types';

import { SITE_ID, SiteIdEnum } from 'src/config-global';
import { useDeviceSyncStore } from 'src/store/DeviceSync';
import useAuthStore from 'src/auth/store/firebase/auth-store';
import { useConnectivityStore } from 'src/features/connectivity/connectivity-store';
import { evaluateOfflineEligibility } from 'src/features/connectivity/offline-eligibility';

import { recordAuthV3Diagnostic } from './diagnostics';
import { runWithAuthV3Retry, classifyAuthV3Error } from './error-policy';
import { startDeviceRefresh, BusinessDeviceV3Error, completeDeviceRefresh } from './api';
import { signChallenge, loadDeviceIdentity, type DeviceIdentity } from './device-identity';
import {
  saveDeviceSession,
  loadDeviceSessionDetailed,
  getDeviceSessionGeneration,
  DeviceSessionGenerationChangedError,
} from './session-store';

// ----------------------------------------------------------------------

export type DeviceSessionRepairOutcome =
  | 'repaired'
  | 'already-present'
  | 'not-applicable'
  | 'offline'
  | 'waiting-for-business'
  | 'storage-unreadable'
  | 'no-identity'
  | 'identity-mismatch'
  | 'definitive-rejection'
  | 'transient-failure'
  | 'throttled'
  | 'superseded';

export type DeviceSessionRepairSnapshot = {
  status: 'idle' | 'checking' | DeviceSessionRepairOutcome;
  lastAttemptAt: string | null;
  lastOutcomeAt: string | null;
  lastSuccessAt: string | null;
  nextRetryAt: string | null;
};

/** Minimum spacing between network repair attempts. */
export const DEVICE_SESSION_REPAIR_MIN_INTERVAL_MS = 5 * 60_000;

// Mirrors the deterministic business-device Firebase UID scheme
// (auth-store.ts BUSINESS_DEVICE_UID_PREFIX / backend buildDeviceFirebaseUid).
const BUSINESS_DEVICE_UID_PREFIX = 'BUSINESS_DEVICE:';

let inFlightRepair: Promise<DeviceSessionRepairOutcome> | null = null;
let lastNetworkAttemptAt = 0;
let terminalForThisRun = false;
let noIdentityReported = false;
let repairSnapshot: DeviceSessionRepairSnapshot = {
  status: 'idle',
  lastAttemptAt: null,
  lastOutcomeAt: null,
  lastSuccessAt: null,
  nextRetryAt: null,
};

export function getDeviceSessionRepairSnapshot(): DeviceSessionRepairSnapshot {
  return { ...repairSnapshot };
}

function publishOutcome(outcome: DeviceSessionRepairOutcome): DeviceSessionRepairOutcome {
  const now = new Date();
  repairSnapshot = {
    status: outcome,
    lastAttemptAt: repairSnapshot.lastAttemptAt,
    lastOutcomeAt: now.toISOString(),
    lastSuccessAt: outcome === 'repaired' ? now.toISOString() : repairSnapshot.lastSuccessAt,
    nextRetryAt:
      outcome === 'transient-failure' || outcome === 'throttled'
        ? new Date(
            Math.max(Date.now(), lastNetworkAttemptAt + DEVICE_SESSION_REPAIR_MIN_INTERVAL_MS)
          ).toISOString()
        : null,
  };
  return outcome;
}

/** Reset process-local throttles/latches when the authenticated device signs out. */
export function resetDeviceSessionRepairState(): void {
  lastNetworkAttemptAt = 0;
  terminalForThisRun = false;
  noIdentityReported = false;
  repairSnapshot = {
    status: 'idle',
    lastAttemptAt: null,
    lastOutcomeAt: null,
    lastSuccessAt: null,
    nextRetryAt: null,
  };
}

export function getDeviceSessionRepairRetryDelayMs(nowMs = Date.now()): number {
  return Math.max(0, lastNetworkAttemptAt + DEVICE_SESSION_REPAIR_MIN_INTERVAL_MS - nowMs);
}

/**
 * Attempt a session repair if — and only if — every gate passes. Single-flight,
 * throttled to one network attempt per {@link DEVICE_SESSION_REPAIR_MIN_INTERVAL_MS},
 * and latched off for the rest of the app run after a definitive backend
 * rejection or an identity mismatch (retrying cannot change those answers).
 *
 * The auth gates make "never during login-screen pairing" structural: while
 * pairing runs the user is not yet authenticated, and pairing persists the
 * session before Firebase sign-in, so `absent` + AUTHENTICATED cannot coincide
 * with an active pairing flow.
 */
export async function maybeRepairDeviceSession(): Promise<DeviceSessionRepairOutcome> {
  if (inFlightRepair) return inFlightRepair;
  inFlightRepair = runRepair().finally(() => {
    inFlightRepair = null;
  });
  return inFlightRepair;
}

async function runRepair(): Promise<DeviceSessionRepairOutcome> {
  repairSnapshot = { ...repairSnapshot, status: 'checking' };
  if (SITE_ID !== SiteIdEnum.PointOfSale || terminalForThisRun) {
    return publishOutcome('not-applicable');
  }
  if (useConnectivityStore.getState().runtimeState !== 'ONLINE') {
    return publishOutcome('offline');
  }

  const auth = useAuthStore.getState();
  if (
    auth.authRestorationStatus !== 'AUTHENTICATED' ||
    !auth.authenticated ||
    !auth.user?.uid.startsWith(BUSINESS_DEVICE_UID_PREFIX)
  ) {
    return publishOutcome('not-applicable');
  }
  const userUid = auth.user.uid;

  // A cold boot can reach ONLINE before Device Sync hydrates the tenant; skip
  // (not latch) so a later pass validates the minted businessId properly.
  const businessId = useDeviceSyncStore.getState().businessData?.id ?? null;
  if (!businessId) return publishOutcome('waiting-for-business');

  // Present sessions need no work. Transiently unreadable storage must not be
  // overwritten, while an absent or structurally invalid record can be safely
  // re-minted after the backend verifies the retained signing identity.
  const read = await loadDeviceSessionDetailed();
  if (read.session) return publishOutcome('already-present');
  if (read.status === 'unreadable') return publishOutcome('storage-unreadable');
  if (Date.now() - lastNetworkAttemptAt < DEVICE_SESSION_REPAIR_MIN_INTERVAL_MS) {
    return publishOutcome('throttled');
  }
  const expectedSessionGeneration = getDeviceSessionGeneration();

  let identity: DeviceIdentity | null = null;
  try {
    identity = await loadDeviceIdentity();
  } catch {
    identity = null;
  }
  if (!identity) {
    if (!noIdentityReported) {
      noIdentityReported = true;
      recordAuthV3Diagnostic('device_session_repair_no_identity', {}, 'warning');
    }
    return publishOutcome('no-identity');
  }
  const deviceIdentity = identity;

  lastNetworkAttemptAt = Date.now();
  repairSnapshot = {
    ...repairSnapshot,
    status: 'checking',
    lastAttemptAt: new Date(lastNetworkAttemptAt).toISOString(),
  };
  recordAuthV3Diagnostic('device_session_repair_started');

  try {
    const refreshOnce = async () => {
      const refresh = await runWithAuthV3Retry(() =>
        startDeviceRefresh({
          publicKeyFingerprint: deviceIdentity.publicKeyFingerprint,
          deviceFingerprint: deviceIdentity.fingerprint,
        })
      );
      const signature = await signChallenge(deviceIdentity.privateKey, refresh.challenge);
      return runWithAuthV3Retry(() =>
        completeDeviceRefresh({ pollingId: refresh.pollingId, signature })
      );
    };

    let result: Awaited<ReturnType<typeof completeDeviceRefresh>>;
    try {
      result = await refreshOnce();
    } catch (refreshError) {
      const challengeExpired =
        refreshError instanceof BusinessDeviceV3Error &&
        (refreshError.errorCode === 'C-56884' ||
          refreshError.errorCode === AuthV3ErrorClassification.PAIRING_PROOF_INVALID);
      if (!challengeExpired) throw refreshError;
      result = await refreshOnce();
    }

    // Validate before any write: the minted record must describe the device
    // Firebase is signed in as, for the tenant this register is synced to.
    if (
      `${BUSINESS_DEVICE_UID_PREFIX}${result.businessDeviceId}` !== userUid ||
      result.businessId !== businessId
    ) {
      terminalForThisRun = true;
      recordAuthV3Diagnostic(
        'device_session_repair_identity_mismatch',
        { mintedBusinessDeviceId: result.businessDeviceId },
        'warning'
      );
      return publishOutcome('identity-mismatch');
    }

    // Logout, tenant switching, connectivity loss, or another session mutation
    // supersedes this network result. Re-check every authority immediately
    // before committing it.
    const currentAuth = useAuthStore.getState();
    const currentBusinessId = useDeviceSyncStore.getState().businessData?.id ?? null;
    if (
      currentAuth.authRestorationStatus !== 'AUTHENTICATED' ||
      !currentAuth.authenticated ||
      currentAuth.user?.uid !== userUid ||
      currentBusinessId !== businessId ||
      useConnectivityStore.getState().runtimeState !== 'ONLINE' ||
      getDeviceSessionGeneration() !== expectedSessionGeneration
    ) {
      recordAuthV3Diagnostic('device_session_repair_superseded');
      return publishOutcome('superseded');
    }

    await saveDeviceSession(
      {
        businessId: result.businessId,
        businessDeviceId: result.businessDeviceId,
        businessLocationId: result.businessLocationId,
        paymentTerminalId: result.paymentTerminalId,
        deviceType: result.deviceType,
        deviceFingerprint: deviceIdentity.fingerprint,
        publicKeyFingerprint: deviceIdentity.publicKeyFingerprint,
        pairedAt: new Date().toISOString(),
      },
      { expectedGeneration: expectedSessionGeneration }
    );
    recordAuthV3Diagnostic('device_session_repair_succeeded');
    // Publish from the repair itself so every trigger (connectivity return,
    // Device Sync hydration, or a manual diagnostic refresh) updates the same
    // eligibility snapshot consumed by V5.
    try {
      const offlineEligibility = await evaluateOfflineEligibility();
      const latestAuth = useAuthStore.getState();
      if (
        latestAuth.authenticated &&
        latestAuth.user?.uid === userUid &&
        getDeviceSessionGeneration() === expectedSessionGeneration
      ) {
        useConnectivityStore.getState().update({ offlineEligibility });
      }
    } catch {
      // The coordinator performs the same safe refresh after a repaired
      // outcome; a local DB read failure here must not undo the session repair.
    }
    return publishOutcome('repaired');
  } catch (error) {
    if (error instanceof DeviceSessionGenerationChangedError) {
      recordAuthV3Diagnostic('device_session_repair_superseded');
      return publishOutcome('superseded');
    }
    const disposition = classifyAuthV3Error(error);
    if (disposition.clearDeviceSession) {
      // DEVICE_NOT_FOUND / DEVICE_REVOKED / DEVICE_KEY_MISMATCH: nothing local
      // to clear (the record is already absent) and no retry can succeed.
      terminalForThisRun = true;
      recordAuthV3Diagnostic(
        'device_session_repair_rejected',
        { classification: disposition.classification },
        'warning'
      );
      return publishOutcome('definitive-rejection');
    }
    recordAuthV3Diagnostic(
      'device_session_repair_failed',
      { classification: disposition.classification },
      'warning'
    );
    return publishOutcome('transient-failure');
  }
}
